import classNames from 'classnames';
import { useMemo } from 'react';
import slugify from 'slugify';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import Loading from './Loading';

import styles from '../../../styles/partials/keyboard.module.scss';

interface KeyboardProps {
    activeLetters?: Array<{
        value?: string;
        valid?: boolean;
    }>;
    loading?: boolean;
    onClick?: (letter: string | null) => void;
    className?: string | null;
}

// const alphabet = [[...'abcdefghij'], [...'klmnopqrs'], [...'tuvwxyz']];

const keyboardAlphabet = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

function Keyboard({
    activeLetters = [],
    loading = false,
    onClick = null,
    className = null,
}: KeyboardProps) {
    const themeClassName = useThemeClassName(styles);

    const finalAlphabet = useMemo(
        () =>
            keyboardAlphabet.map((row) =>
                row.map((letter) => {
                    const active =
                        (activeLetters || []).find(
                            ({ value = null }) => slugify(value) === slugify(letter),
                        ) || null;
                    return { value: letter, valid: active !== null ? active.valid : null };
                }),
            ),
        [activeLetters],
    );

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            {finalAlphabet.map((row, index) => (
                <div key={`row-${index + 1}`} className={styles.row}>
                    {row.map(({ value: letter = null, valid = null }) => (
                        <Button
                            key={`letter-${letter}`}
                            className={classNames([
                                styles.button,
                                {
                                    [styles.active]: valid !== null,
                                    [styles.true]: valid === true,
                                    [styles.false]: valid === false,
                                },
                            ])}
                            disabled={valid !== null || loading}
                            onClick={() => onClick(letter)}
                        >
                            {letter}
                        </Button>
                    ))}
                </div>
            ))}
            {loading ? <Loading loading={loading} className={styles.loading} /> : null}
        </div>
    );
}

export default Keyboard;
